import { useNavigation } from '@react-navigation/native'
import { Platform } from 'react-native'

import { usePreviousRouteName } from 'features/navigation/helpers/usePreviousRouteName'
import {
  RootStackParamList,
  UseNavigationType,
} from 'features/navigation/navigators/RootNavigator/types'

/**
 * Go back to the previous route in history,
 * or go back to some specified route if no previous route exists.
 *
 * @param ...navigateParams - Same parameters as navigate(...) function.
 */
export const useGoBack = <RouteName extends keyof RootStackParamList>(
  ..._navigateParams: undefined extends RootStackParamList[RouteName]
    ? [RouteName] | [RouteName, RootStackParamList[RouteName]]
    : [RouteName] | [RouteName, RootStackParamList[RouteName]]
) => {
  const { canGoBack, goBack, popTo } = useNavigation<UseNavigationType>()
  const previousRouteName = usePreviousRouteName()

  const customGoBack = () => {
    const can = canGoBack()
    if (typeof previousRouteName !== 'undefined' && can) {
      goBack()
    } else if (can && Platform.OS === 'web') {
      window.history.back()
    } else {
      // TypeScript cannot verify that our union type matches navigate's overloaded signature
      // but the types are structurally correct - we're using the same conditional type pattern
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      popTo(_navigateParams[0] as any, _navigateParams[1])
    }
  }

  const customCanGoBack = (): boolean => {
    if (Platform.OS !== 'web') {
      return canGoBack()
    }
    let can = canGoBack()
    if (!can && window) {
      can = window.history.length > 2 // checked on FF & Chrome and it's 2
    }
    return can
  }

  return { goBack: customGoBack, canGoBack: customCanGoBack }
}
